/**
 * Royal Carnet Flipbook
 * ---------------------------------------------------------------------------
 * Navigation, prechargement intelligent des images, accessibilite clavier,
 * mode plein ecran et zoom sur les photos.
 *
 * SOMMAIRE
 * 1. Prechargement des images (data-src -> src)
 * 2. Navigation (boutons, clavier, tactile, clic sur le livre)
 * 3. Animation de retournement (depth flip en keyframes + will-change cible)
 * 4. Accessibilite + mode lecture immersif (inert, header/footer masques
 *    automatiquement pendant qu'une page produit est ouverte)
 * 5. Mode plein ecran (avec repli gracieux si non supporte, ex. iOS Safari)
 * 6. Zoom image (lightbox accessible)
 * 7. Ajout au panier en AJAX avec confirmation (le bouton "Acheter
 *    maintenant" reste une vraie soumission de formulaire pour rediriger
 *    vers le checkout)
 * 7bis. "Voir plus" pour les descriptions tronquees
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  /** @type {Object<string, {destroy: function}>} Instances actives, indexees par section.id */
  var instances = {};

  var prefersReducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  // ---------------------------------------------------------------------
  // 1. Prechargement des images
  // ---------------------------------------------------------------------

  /**
   * Active l'image reelle d'une page (data-src -> src) si ce n'est pas
   * deja fait. Ne fait rien si la page n'existe pas ou est deja chargee.
   * @param {HTMLElement[]} pages
   * @param {number} index
   */
  function preloadPage(pages, index) {
    var page = pages[index];
    if (!page) return;

    var img = page.querySelector('img[data-src]');
    if (!img) return;

    img.loading = 'eager';
    img.decoding = 'async';

    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
      img.removeAttribute('data-srcset');
    }

    img.src = img.dataset.src;
    img.removeAttribute('data-src');
  }

  /**
   * Precharge la page courante et ses deux voisines immediates, pour que
   * le retournement de page paraisse instantane.
   * @param {HTMLElement[]} pages
   * @param {number} current
   */
  function preloadNeighbors(pages, current) {
    preloadPage(pages, current);
    preloadPage(pages, current + 1);
    preloadPage(pages, current - 1);
  }

  // ---------------------------------------------------------------------
  // Instance principale
  // ---------------------------------------------------------------------

  /**
   * Initialise une instance du flipbook pour une section donnee.
   * @param {string} sectionId
   */
  function initInstance(sectionId) {
    var root = document.getElementById('rf2-' + sectionId);
    if (!root) return;

    var book = root.querySelector('[data-rf2-book]');
    var pages = Array.from(root.querySelectorAll('[data-rf2-page]'));
    var prevBtns = Array.from(root.querySelectorAll('[data-rf2-prev]'));
    var nextBtns = Array.from(root.querySelectorAll('[data-rf2-next]'));
    var fsBtn = root.querySelector('[data-rf2-fullscreen]');
    var toastEl = root.querySelector('[data-rf2-toast]');

    if (!book || pages.length === 0) return;

    var total = pages.length;
    var current = 0; // index de la page active (0 = couverture)
    var cleanupFns = [];

    /** Enregistre un ecouteur et sa fonction de nettoyage, pour destroy(). */
    function on(target, type, handler, opts) {
      target.addEventListener(type, handler, opts);
      cleanupFns.push(function () { target.removeEventListener(type, handler, opts); });
    }

    // -----------------------------------------------------------------
    // 4. Accessibilite : une seule page "reelle" a la fois pour le
    // clavier et les lecteurs d'ecran. Les pages empilees derriere sont
    // rendues inertes (inert + aria-hidden) pour ne pas etre atteignables
    // au Tab ni annoncees, meme si elles restent presentes dans le DOM
    // (donc toujours indexables par les moteurs de recherche).
    //
    // Cette meme fonction pilote aussi le mode "lecture immersive" :
    // tant que current > 0 (une page produit est ouverte), la classe
    // rf2-reading masque le header et le footer du theme (voir CSS) pour
    // une experience plein ecran sans distraction. Des qu'on revient a
    // la couverture (current === 0), ils reapparaissent.
    //
    // aria-hidden pilote egalement une optimisation critique de
    // performance en CSS (content-visibility: hidden sur les pages
    // inactives), qui evite a Safari de calculer inutilement les ombres,
    // degrades et couches 3D de 13 pages en meme temps — un cas connu de
    // plantage/rechargement force de l'onglet sur iOS, meme sur des
    // appareils recents, independamment de la puissance du telephone.
    // -----------------------------------------------------------------
    function updateActiveState() {
      pages.forEach(function (page, i) {
        var isActive = i === current;
        if ('inert' in page) page.inert = !isActive;
        page.setAttribute('aria-hidden', String(!isActive));

        // Bug corrige : le z-index de chaque page etait fige a la
        // generation de la section (le plus eleve pour la couverture),
        // et ne changeait jamais ensuite. Une fois tournee, la
        // couverture gardait donc le z-index le plus haut de toute la
        // pile et pouvait repasser au-dessus des pages produits au
        // moindre alea de rendu pendant l'animation. On recalcule
        // desormais le z-index a chaque navigation : les pages deja
        // tournees (i < current, couverture incluse) sont toujours
        // reléguées sous les pages non tournees, quel que soit l'ordre
        // dans lequel elles ont ete generees.
        page.style.zIndex = i < current ? i : (2 * total) - i;
      });

      prevBtns.forEach(function (btn) { btn.disabled = current <= 0; });
      nextBtns.forEach(function (btn) { btn.disabled = current >= total - 1; });

      document.body.classList.toggle('rf2-reading', current > 0);
    }

    // -----------------------------------------------------------------
    // 3. Animation de retournement avec effet de profondeur
    // -----------------------------------------------------------------

    /**
     * Anime le retournement d'une page unique. `will-change` n'est
     * applique que sur la page concernee, le temps de l'animation, pour
     * ne pas forcer une couche de composition GPU sur toutes les pages
     * en permanence.
     * @param {number} pageIndex
     * @param {'forward'|'backward'} direction
     */
    function animateFlip(pageIndex, direction) {
      var page = pages[pageIndex];
      if (!page) return;

      if (prefersReducedMotion) {
        page.classList.toggle('rf2-flipped', direction === 'forward');
        return;
      }

      var animClass = direction === 'forward' ? 'rf2-animating-forward' : 'rf2-animating-backward';
      page.classList.add('rf2-will-animate', animClass);

      var onAnimationEnd = function () {
        page.removeEventListener('animationend', onAnimationEnd);
        page.classList.remove('rf2-will-animate', animClass);
        page.classList.toggle('rf2-flipped', direction === 'forward');
      };
      page.addEventListener('animationend', onAnimationEnd);
    }

    /** Petit effet de "livre qui s'ouvre" la premiere fois qu'on quitte la couverture. */
    function playOpeningEffect(flippingIndex) {
      if (flippingIndex !== 0) return;
      book.classList.add('rf2-opening');
      window.setTimeout(function () { book.classList.remove('rf2-opening'); }, 900);
    }

    // -----------------------------------------------------------------
    // 2. Navigation
    // -----------------------------------------------------------------

    function goNext() {
      if (current >= total - 1) return;
      var flippingIndex = current;
      current += 1;
      animateFlip(flippingIndex, 'forward');
      updateActiveState();
      preloadNeighbors(pages, current);
      playOpeningEffect(flippingIndex);
      checkActivePageClamp();
    }

    function goPrev() {
      if (current <= 0) return;
      current -= 1;
      animateFlip(current, 'backward');
      updateActiveState();
      preloadNeighbors(pages, current);
      checkActivePageClamp();
    }

    nextBtns.forEach(function (btn) {
      on(btn, 'click', function (e) { e.preventDefault(); goNext(); });
    });
    prevBtns.forEach(function (btn) {
      on(btn, 'click', function (e) { e.preventDefault(); goPrev(); });
    });

    // Navigation clavier : desactivee si le carnet est hors ecran, si le
    // focus est dans un champ de formulaire (ex. selecteur de variante),
    // ou si la loupe (lightbox) est ouverte.
    on(document, 'keydown', function (e) {
      var active = document.activeElement;
      if (active && /^(INPUT|SELECT|TEXTAREA)$/.test(active.tagName)) return;
      if (lightbox && !lightbox.hidden) return;

      var rect = root.getBoundingClientRect();
      var inView = rect.top < window.innerHeight && rect.bottom > 0;
      if (!inView) return;

      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
    });

    // Navigation tactile (swipe horizontal)
    var touchStartX = null;
    var touchStartY = null;

    on(book, 'touchstart', function (e) {
      var t = e.changedTouches[0];
      touchStartX = t.clientX;
      touchStartY = t.clientY;
    }, { passive: true });

    on(book, 'touchend', function (e) {
      if (touchStartX === null) return;
      var t = e.changedTouches[0];
      var dx = t.clientX - touchStartX;
      var dy = t.clientY - touchStartY;

      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) goNext();
        else goPrev();
      }
      touchStartX = null;
      touchStartY = null;
    }, { passive: true });

    // Clic sur les zones gauche/droite du livre pour tourner la page.
    // Ignore les clics sur les elements interactifs (boutons, liens,
    // formulaire, selecteur de variante, loupe de zoom...).
    on(book, 'click', function (e) {
      if (e.target.closest('a, button, select, input, form, label')) return;
      var rect = book.getBoundingClientRect();
      var x = e.clientX - rect.left;
      if (x > rect.width * 0.6) goNext();
      else if (x < rect.width * 0.4 && current > 0) goPrev();
    });

    // -----------------------------------------------------------------
    // Prechargement initial : uniquement quand le carnet approche de la
    // fenetre d'affichage (evite de charger des images hors ecran).
    // -----------------------------------------------------------------
    var observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            preloadNeighbors(pages, current);
            observer.disconnect();
          }
        });
      }, { rootMargin: '200px' });
      observer.observe(root);
    } else {
      preloadNeighbors(pages, current);
    }

    // -----------------------------------------------------------------
    // 5. Mode plein ecran
    // -----------------------------------------------------------------
    var fullscreenSupported = !!(
      document.fullscreenEnabled || document.webkitFullscreenEnabled
    );

    if (fsBtn) {
      if (!fullscreenSupported) {
        // Ex. iOS Safari ne supporte pas la Fullscreen API sur un
        // element quelconque : on masque le bouton plutot que de
        // proposer une action qui ne fonctionnerait pas.
        fsBtn.hidden = true;
      } else {
        on(fsBtn, 'click', function () {
          var isFs = document.fullscreenElement || document.webkitFullscreenElement;
          if (!isFs) {
            var request = root.requestFullscreen || root.webkitRequestFullscreen;
            if (request) request.call(root);
          } else {
            var exit = document.exitFullscreen || document.webkitExitFullscreen;
            if (exit) exit.call(document);
          }
        });

        var handleFullscreenChange = function () {
          var isFs = !!(document.fullscreenElement || document.webkitFullscreenElement);
          root.classList.toggle('rf2-is-fullscreen', isFs);
          fsBtn.setAttribute('aria-pressed', String(isFs));
        };
        on(document, 'fullscreenchange', handleFullscreenChange);
        on(document, 'webkitfullscreenchange', handleFullscreenChange);
      }
    }

    // -----------------------------------------------------------------
    // 6. Zoom image (lightbox)
    // -----------------------------------------------------------------
    var lightbox = root.querySelector('[data-rf2-lightbox]');
    var lightboxImg = lightbox ? lightbox.querySelector('[data-rf2-lightbox-img]') : null;
    var lightboxCloseBtn = lightbox ? lightbox.querySelector('[data-rf2-lightbox-close]') : null;
    var lastFocusedBeforeZoom = null;

    function openLightbox(zoomSrc, alt, triggerEl) {
      if (!lightbox || !lightboxImg || !zoomSrc) return;
      lightboxImg.src = zoomSrc;
      lightboxImg.alt = alt || '';
      lightbox.hidden = false;
      lastFocusedBeforeZoom = triggerEl || document.activeElement;
      if (lightboxCloseBtn) lightboxCloseBtn.focus();
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.hidden = true;
      lightboxImg.removeAttribute('src');
      if (lastFocusedBeforeZoom) lastFocusedBeforeZoom.focus();
    }

    if (lightbox && lightboxImg) {
      var zoomTriggers = Array.from(root.querySelectorAll('[data-rf2-zoom]'));
      zoomTriggers.forEach(function (trigger) {
        on(trigger, 'click', function () {
          openLightbox(trigger.dataset.zoomSrc, trigger.dataset.zoomAlt, trigger);
        });
      });

      if (lightboxCloseBtn) on(lightboxCloseBtn, 'click', closeLightbox);

      on(lightbox, 'click', function (e) {
        if (e.target === lightbox) closeLightbox();
      });

      on(document, 'keydown', function (e) {
        if (e.key === 'Escape' && !lightbox.hidden) closeLightbox();
      });
    }

    // -----------------------------------------------------------------
    // 7. Ajout au panier en AJAX (le bouton Acheter maintenant garde une
    // vraie soumission de formulaire pour rediriger vers le checkout)
    // -----------------------------------------------------------------
    function showToast(message, isError) {
      if (!toastEl) return;
      toastEl.textContent = message;
      toastEl.classList.toggle('rf2-toast--error', !!isError);
      toastEl.classList.add('rf2-toast--visible');
      window.clearTimeout(toastEl._rf2HideTimer);
      toastEl._rf2HideTimer = window.setTimeout(function () {
        toastEl.classList.remove('rf2-toast--visible');
      }, 3200);
    }

    var forms = Array.from(root.querySelectorAll('.rf2-form'));
    forms.forEach(function (form) {
      on(form, 'submit', function (e) {
        // Seul le bouton "Ajouter au panier" est intercepte en AJAX.
        // Le bouton "Acheter maintenant" (name="return_to") doit
        // continuer sa soumission normale pour rediriger vers le
        // checkout. Sans e.submitter (tres vieux navigateurs), on
        // laisse la soumission classique se faire (repli fonctionnel).
        if (!e.submitter || !e.submitter.classList.contains('rf2-btn-cart')) return;

        e.preventDefault();
        var btn = e.submitter;
        var originalLabel = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Ajout…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(Object.fromEntries(new FormData(form)))
        })
          .then(function (res) {
            return res.json().then(function (data) { return { ok: res.ok, data: data }; });
          })
          .then(function (result) {
            btn.disabled = false;
            btn.textContent = originalLabel;
            if (!result.ok) {
              showToast((result.data && result.data.description) || 'Une erreur est survenue.', true);
              return;
            }
            showToast('Ajouté au carnet.', false);
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: result.data }));
          })
          .catch(function () {
            btn.disabled = false;
            btn.textContent = originalLabel;
            showToast('Une erreur est survenue. Réessayez.', true);
          });
      });
    });

    // -----------------------------------------------------------------
    // 7bis. "Voir plus" pour les descriptions tronquees a 2-3 lignes.
    // Ne montre le bouton que si le texte est reellement coupe.
    //
    // Important : la mesure (scrollHeight/clientHeight) ne peut se faire
    // que sur une page reellement rendue. Comme les pages inactives
    // passent desormais en content-visibility:hidden (voir CSS, fix
    // crash iOS), leur mesure renverrait toujours 0. On ne verifie donc
    // plus toutes les pages une seule fois au chargement, mais seulement
    // la page qui devient active, a chaque navigation.
    // -----------------------------------------------------------------
    var textWraps = Array.from(root.querySelectorAll('[data-rf2-textwrap]'));
    textWraps.forEach(function (wrap) {
      var text = wrap.querySelector('.rf2-page-text');
      var btn = wrap.querySelector('[data-rf2-readmore]');
      if (!text || !btn) return;

      on(btn, 'click', function () {
        var expanded = wrap.classList.toggle('is-expanded');
        text.classList.toggle('rf2-page-text--full', expanded);
        btn.textContent = expanded ? 'Voir moins' : 'Voir plus';
      });
    });

    function checkActivePageClamp() {
      var page = pages[current];
      if (!page) return;
      var wrap = page.querySelector('[data-rf2-textwrap]');
      if (!wrap || wrap.classList.contains('is-clamped')) return;
      var text = wrap.querySelector('.rf2-page-text');
      var btn = wrap.querySelector('[data-rf2-readmore]');
      if (!text || !btn) return;

      window.requestAnimationFrame(function () {
        if (text.scrollHeight - text.clientHeight > 2) {
          wrap.classList.add('is-clamped');
          btn.hidden = false;
        }
      });
    }

    // -----------------------------------------------------------------
    updateActiveState();
    checkActivePageClamp();

    instances[sectionId] = {
      destroy: function () {
        cleanupFns.forEach(function (fn) { fn(); });
        if (observer) observer.disconnect();
        document.body.classList.remove('rf2-reading');
      }
    };
  }

  window.RoyalCarnetFlipbook = {
    /**
     * Point d'entree public. Detruit proprement une instance existante
     * avant d'en recreer une (utile lors des re-rendus de l'editeur de
     * theme), pour ne jamais dupliquer d'ecouteurs d'evenements.
     * @param {string} sectionId
     */
    init: function (sectionId) {
      if (instances[sectionId]) {
        instances[sectionId].destroy();
        delete instances[sectionId];
      }
      initInstance(sectionId);
    }
  };
})();
